/**
 * Dashboard WebSocket Hook
 * Manages real-time dashboard updates via WebSocket
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:7503';
const WS_ENABLED = import.meta.env.VITE_WS_ENABLED !== 'false'; // Set to false to disable WebSocket

export const useDashboardWebSocket = (onUpdate) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3; // Stop trying after 3 failed attempts

  const connect = useCallback(() => {
    // Check if WebSocket is enabled
    if (!WS_ENABLED) {
      console.info('WebSocket is disabled. Dashboard will use polling for updates.');
      setIsConnected(false);
      return;
    }

    // Check reconnect attempts
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.warn('Max WebSocket reconnection attempts reached. Falling back to polling.');
      setIsConnected(false);
      return;
    }

    try {
      // Get auth token (check both 'token' and 'access_token')
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      if (!token) {
        console.warn('No auth token found - WebSocket will not connect');
        // Don't return - continue to show disconnected state
        setIsConnected(false);
        return;
      }

      console.log('Attempting WebSocket connection...');
      reconnectAttempts.current++;

      // Create WebSocket connection
      ws.current = new WebSocket(`${WS_URL}/ws/dashboard/`);

      ws.current.onopen = () => {
        console.log('✅ Dashboard WebSocket connected successfully');
        setIsConnected(true);
        reconnectAttempts.current = 0; // Reset counter on successful connection

        // Start ping interval to keep connection alive
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({
              type: 'ping',
              timestamp: new Date().toISOString()
            }));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastUpdate(new Date());

          // Handle different message types
          switch (data.type) {
            case 'connection_established':
              console.log('Dashboard WebSocket: Connection established', data.message);
              break;

            case 'dashboard_update':
              // Full dashboard update
              if (onUpdate && data.data) {
                onUpdate(data.data);
              }
              break;

            case 'live_truck_update':
              // Live truck status update
              if (onUpdate && data.data) {
                onUpdate({ live_trucks: data.data });
              }
              break;

            case 'dock_update':
              // Dock utilization update
              if (onUpdate && data.data) {
                onUpdate({ dock_utilization: data.data });
              }
              break;

            case 'qc_update':
              // QC status update
              if (onUpdate && data.data) {
                onUpdate({ qc_status: data.data });
              }
              break;

            case 'alert_update':
              // New alert
              if (onUpdate && data.data) {
                onUpdate({ new_alert: data.data });
              }
              break;

            case 'metrics_update':
              // Metrics update
              if (onUpdate && data.data) {
                onUpdate({ todays_metrics: data.data });
              }
              break;

            case 'pong':
              // Ping response
              break;

            default:
              console.log('Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('Dashboard WebSocket error:', error);
      };

      ws.current.onclose = () => {
        console.log('Dashboard WebSocket closed');
        setIsConnected(false);

        // Clear ping interval
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }

        // Only attempt to reconnect if under max attempts
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectTimeout.current = setTimeout(() => {
            console.log(`Attempting to reconnect dashboard WebSocket (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})...`);
            connect();
          }, 5000);
        } else {
          console.info('WebSocket reconnection stopped. Dashboard will use 30-second polling for updates.');
        }
      };
    } catch (error) {
      console.error('Error connecting dashboard WebSocket:', error);
    }
  }, [onUpdate]);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }

    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }

    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }

    setIsConnected(false);
  }, []);

  const requestUpdate = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'request_update'
      }));
    }
  }, []);

  const subscribe = useCallback((section) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'subscribe',
        section: section
      }));
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    lastUpdate,
    requestUpdate,
    subscribe
  };
};
